import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Trash2, Save, RefreshCw } from 'lucide-react';
import { fetchNmResponsableOverrides, editNmResponsableOverride, deleteNmResponsableOverride, fetchPersonasCatalog } from '@/services/api';
import { PersonaCombobox } from '@/components/common/PersonaCombobox';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

// P0 03/09: hasta ahora, una vez guardado un override de "responsable" desde
// Admin -> Sin Sede -> Resp. Otro, el NM salía de esa cola y no había NINGUNA
// pantalla para corregirlo o borrarlo si se guardó mal (ej. nombre duplicado
// por un input con lag). Esta pestaña lista TODOS los overrides guardados,
// no solo los pendientes, y permite editarlos/borrarlos por objectid/GlobalID.
export function RespOverridesTab() {
  const qc = useQueryClient();
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['nm-responsable-overrides'],
    queryFn: fetchNmResponsableOverrides,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
  const items = data?.items || [];
  const { data: personasCatalog = [] } = useQuery({
    queryKey: ['personas-catalog'],
    queryFn: fetchPersonasCatalog,
    staleTime: 300000,
  });

  const saveMut = useMutation({
    mutationFn: ({ objectid, nombre }) => editNmResponsableOverride(objectid, { responsable_nombre: nombre }),
    onMutate: ({ objectid }) => setBusy(objectid),
    onSuccess: (res, { objectid }) => {
      toast.success(`Override ${objectid} actualizado a "${res.responsable_nombre}"`);
      setEdits(prev => { const n = { ...prev }; delete n[objectid]; return n; });
      qc.invalidateQueries({ queryKey: ['nm-responsable-overrides'] });
      qc.invalidateQueries({ queryKey: ['nm-responsable-otro'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Error al guardar'),
    onSettled: () => setBusy(null),
  });

  const deleteMut = useMutation({
    mutationFn: (objectid) => deleteNmResponsableOverride(objectid),
    onMutate: (objectid) => setBusy(objectid),
    onSuccess: (_res, objectid) => {
      toast.success(`Override ${objectid} borrado — el NM vuelve a "Otro"`);
      qc.invalidateQueries({ queryKey: ['nm-responsable-overrides'] });
      qc.invalidateQueries({ queryKey: ['nm-responsable-otro'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Error al borrar'),
    onSettled: () => setBusy(null),
  });

  return (
    <div className="space-y-4" data-testid="resp-overrides-tab">
      <div>
        <h3 className="font-montserrat font-semibold text-sm" style={{ color: 'var(--vz-text-primary)' }}>
          Overrides de responsable NM ({data?.total ?? '…'})
        </h3>
        <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
          Todos los nombres de responsable resueltos a mano desde "Resp. Otro" — editables y borrables aquí, incluso los que ya salieron de esa cola.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <CheckCircle size={32} style={{ color: 'var(--vz-green)' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--vz-text-primary)' }}>Aún no hay overrides de responsable guardados</p>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid="resp-overrides-table">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'var(--vz-bg-secondary)', borderColor: 'var(--vz-border-subtle)' }}>
                <TableHead className="text-xs font-semibold">Objectid</TableHead>
                <TableHead className="text-xs font-semibold">Fecha</TableHead>
                <TableHead className="text-xs font-semibold">Centro</TableHead>
                <TableHead className="text-xs font-semibold">Descripción</TableHead>
                <TableHead className="text-xs font-semibold min-w-[260px]">Nombre del responsable</TableHead>
                <TableHead className="text-xs font-semibold">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((ov) => {
                const objectid = ov.objectid;
                const value = edits[objectid] ?? ov.responsable_nombre ?? '';
                const dirty = value.trim() !== (ov.responsable_nombre || '').trim();
                return (
                  <TableRow key={objectid} style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid={`resp-override-row-${objectid}`}>
                    <TableCell className="text-xs font-mono" style={{ color: 'var(--vz-text-muted)' }}>{objectid}</TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{ov.fecha_reporte ? String(ov.fecha_reporte).slice(0, 10) : '—'}</TableCell>
                    <TableCell className="text-xs font-medium" style={{ color: 'var(--vz-text-primary)' }}>{ov.centro_nombre || '—'}</TableCell>
                    <TableCell className="text-xs max-w-[240px] truncate" style={{ color: 'var(--vz-text-muted)' }} title={ov.descripcion}>{ov.descripcion || '—'}</TableCell>
                    <TableCell>
                      <PersonaCombobox
                        personas={personasCatalog}
                        value={value}
                        onSelect={nombre => setEdits(prev => ({ ...prev, [objectid]: nombre }))}
                        testId={`resp-override-select-${objectid}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm" className="h-7 px-2 text-xs gap-1"
                          disabled={!dirty || busy === objectid}
                          onClick={() => saveMut.mutate({ objectid, nombre: value })}
                          data-testid={`resp-override-save-btn-${objectid}`}
                        >
                          {busy === objectid && saveMut.isPending ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
                        </Button>
                        <Button
                          size="sm" variant="destructive" className="h-7 px-2 text-xs gap-1"
                          disabled={busy === objectid}
                          onClick={() => { if (window.confirm(`¿Borrar el override del NM ${objectid}? Volverá a "Otro".`)) deleteMut.mutate(objectid); }}
                          data-testid={`resp-override-delete-btn-${objectid}`}
                        >
                          {busy === objectid && deleteMut.isPending ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
